import { Component, HostListener, OnInit, computed, signal } from '@angular/core';
import { SurveyHeaderComponent } from '../../components/survey-header/survey-header.component';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { PageLoaderComponent } from '../../../../core/layout/components/page-loader/page-loader.component';
import { ApiSurveyDto } from '../../../../shared/services/api-surveymodel.service';
import { Router } from '@angular/router';
import { CanDeactivateComponent } from '../survey-builder/survey-builder.guard';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { FormsModule } from '@angular/forms';
import { ApiPutSurveyTranslationsGlobalResponse, ApiSurveyTranslationsGlobalService } from '../../../../shared/services/api-surveytranslationsglobal.service';
import { GlobalTranslationsResponse, AdditionalTranslation } from '../../../../shared/models/globaltranslations-response.interface';
import { SupportedLanguage } from '../../../../shared/models/supportedLanguage.interface';

@Component({
  selector: 'app-global-translations',
  templateUrl: './global-translations.component.html',
  styleUrls: ['./global-translations.component.css'],
  imports: [FormsModule, SurveyHeaderComponent, BreadcrumbComponent, PageLoaderComponent]
})

export class GlobalTranslationsComponent implements OnInit, CanDeactivateComponent {
  survey: ApiSurveyDto | null = null;
  translations: GlobalTranslationsResponse | null = null;
  loading = signal(false);
  error = signal<string | null>(null);
  defaultLanguage: string = 'en';
  previousLanguage: string | null = null;
  supportedLanguages: Array<{ lang: string } & SupportedLanguage> = [];
  globalTranslation: AdditionalTranslation[] = [];
  private originalSnapshot = new Map<string, string>();
  saving = signal(false);
  saveError = signal<string | null>(null);
  showSaveResult = signal<{ success: number; failed: number } | null>(null);
  showSaveConfirm = signal(false);
  showDiscardConfirm = signal(false);
  showLeaveConfirm = signal(false);
  private leaveResolve: ((allow: boolean) => void) | null = null;
  showLanguageChangeConfirm = signal(false);
  private expandedCategory: string | null = null;
  
  readonly canEdit = computed(() => this.userStore.isAdmin());

  constructor(
    private router: Router,
    private organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private globalTranslationsService: ApiSurveyTranslationsGlobalService) {}

  ngOnInit() {
    const organizationId = this.organizationStore.selectedOrganizationId();

    if (!organizationId) {
      this.router.navigate(['/organizations']);
      return;
    }

    this.loadTranslationsModel(organizationId);
  }

  private loadTranslationsModel(organizationId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.globalTranslationsService.getSurveyTranslationsGlobal(organizationId, null).subscribe({
      next: (translations) => {
        // Set translations
        this.translations = translations;

        // Sort by category then label
        this.translations.additionalTranslations.sort((a, b) => {
          const cat = a.category.localeCompare(b.category);
          return cat !== 0 ? cat : a.label.localeCompare(b.label);
        });

        if(this.previousLanguage){
          this.defaultLanguage = this.previousLanguage;
        }
        else {
          this.defaultLanguage = ['en', 'en-GB', 'en-US'].find(key => key in translations.supportedLanguages) ?? Object.keys(translations.supportedLanguages)[0];
          this.previousLanguage = this.defaultLanguage;
        }

        this.supportedLanguages = Object.entries(translations.supportedLanguages).map(([key, value]) => ({
          lang: key,
          ...value
        }));

        this.changeTranslations();
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load survey translations. | ' + err);
        this.loading.set(false);
      }
    });
  }
  
  changeTranslations(): void {
    this.globalTranslation = this.translations?.additionalTranslations
      .filter(t => t.culture == this.defaultLanguage)
      .map(t => ({ ...t })) || [];
    this.takeSnapshot();

    this.expandedCategory = null;
  }

  formatCategory(category: string): string {
    return category.replace(/^surveyenginev2:/i, '');
  }

  get translationsByCategory(): { category: string; items: AdditionalTranslation[] }[] {
    const map = new Map<string, AdditionalTranslation[]>();
    for (const g of this.globalTranslation) {
      if (!map.has(g.category)) map.set(g.category, []);
      map.get(g.category)!.push(g);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({ category, items }));
  }

  toggleCategory(category: string): void {
    this.expandedCategory = this.expandedCategory === category ? null : category;
    if (this.expandedCategory === category) {
      this.scrollToCategory(category);
    }
  }

  isCategoryCollapsed(category: string): boolean {
    return this.expandedCategory !== category;
  }

  categoryAnchorId(category: string): string {
    return 'category-' + category.replace(/[^a-zA-Z0-9]+/g, '-');
  }

  private scrollToCategory(category: string): void {
    setTimeout(() => {
      document.getElementById(this.categoryAnchorId(category))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  categoryChangeCount(items: AdditionalTranslation[]): number {
    return items.filter(g => this.isTranslationChanged(g.label, g.category)).length;
  }

  private takeSnapshot(): void {
    this.originalSnapshot.clear();
    this.globalTranslation.forEach(g => this.originalSnapshot.set(`${g.label}|${g.category}`, g.localizedText));
  }

  isTranslationChanged(label: string, category: string): boolean {
    const original = this.originalSnapshot.get(`${label}|${category}`);
    if (original === undefined) return false;
    const g = this.globalTranslation.find(g => g.label === label && g.category === category);
    if (!g) return false;
    return (g.localizedText ?? '') !== original;
  }

  get pendingChangesCount(): number {
    let count = 0;
    for (const g of this.globalTranslation) {
      if (this.originalSnapshot.get(`${g.label}|${g.category}`) !== (g.localizedText ?? '')) count++;
    }
    return count;
  }

  requestSave(): void {
    if (!this.canEdit()) return;
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.showSaveConfirm.set(true);
  }

  cancelSave(): void {
    if (this.saving()) return;
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.showSaveConfirm.set(false);
  }

  save(): void {
    if (!this.canEdit()) return;
    const organizationId = this.organizationStore.selectedOrganizationId();
    if (!organizationId) return;

    const changed: AdditionalTranslation[] = [];
    for (const g of this.globalTranslation) {
      if (this.originalSnapshot.get(`${g.label}|${g.category}`) !== (g.localizedText ?? '')) {
        changed.push({ label: g.label, culture: this.defaultLanguage, category: g.category, localizedText: g.localizedText ?? '' });
      }
    }

    if (changed.length === 0) return;

    this.saving.set(true);
    this.saveError.set(null);
    this.showSaveResult.set(null);
    this.globalTranslationsService.updateSurveyTranslations(organizationId, changed).subscribe({
      next: (response: ApiPutSurveyTranslationsGlobalResponse) => {
        this.saving.set(false);
        this.showSaveConfirm.set(false);
        const success = response.additionalTranslations.filter(t => t.result.status === 'OK').length;
        const failed = response.additionalTranslations.filter(t => t.result.status === 'ERROR').length;
        this.showSaveResult.set({ success, failed });
        this.loadTranslationsModel(organizationId);
      },
      error: (err) => {
        this.saving.set(false);
        this.saveError.set(err?.message ?? 'Failed to save global translations.');
      }
    });
  }

  requestDiscard(): void {
    if (!this.canEdit()) return;
    this.showDiscardConfirm.set(true);
  }

  cancelDiscard(): void {
    this.showDiscardConfirm.set(false);
  }

  discard(): void {
    if (!this.canEdit()) return;
    this.changeTranslations();
    this.showDiscardConfirm.set(false);
  }

  closeSaveResult(): void {
    this.showSaveResult.set(null);
  }

  requestLanguageChange(): void {
    if (this.pendingChangesCount === 0) {
      this.previousLanguage = this.defaultLanguage;
      this.changeTranslations();
      return;
    }
    this.showLanguageChangeConfirm.set(true);
  }

  confirmLanguageChange(): void {
    this.previousLanguage = this.defaultLanguage;
    this.changeTranslations();
    this.showLanguageChangeConfirm.set(false);
  }

  cancelLanguageChange(): void {
    this.defaultLanguage = this.previousLanguage ?? "en";
    this.showLanguageChangeConfirm.set(false);
  }

  setLeaveResolve(fn: (allow: boolean) => void): void {
    this.leaveResolve = fn;
  }

  confirmLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(true);
    this.leaveResolve = null;
  }

  cancelLeave(): void {
    this.showLeaveConfirm.set(false);
    this.leaveResolve?.(false);
    this.leaveResolve = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.pendingChangesCount > 0) {
      event.preventDefault();
    }
  }
}
